import {
  Box,
  Chip,
  Grid,
  ListItem,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { styled } from "@mui/system";
import { Link } from "react-router-dom";
import NAV from "../../utils/nav.const";
import theme from "../../styles/styleTheme";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import StraightenIcon from "@mui/icons-material/Straighten";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/currency";
import { useState } from "react";

type CollectionCardProps = {
  property: any;
  loading: boolean;
  index: any;
  size?: any;
};

const CollectionCard = ({
  property,
  loading,
  index,
  size,
}: CollectionCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  let formattedDate = "";

  moment.locale("en");
  const availableFromDate = moment(property?.propertyDetails?.availableFromDate ?? property?.availableFromDate);
  if (!availableFromDate.isValid()) {
    formattedDate = t("overview.available_soon");
  } else {
    formattedDate = availableFromDate.isBefore(moment())
      ? t("overview.directly_available")
      : t("overview.available_from") +
        ` ${availableFromDate.format("DD-MM-yyyy")}`;
  }

  return (
    <>
      {loading ? (
        <Skeleton
          variant="rectangular"
          height="10.5rem"
          style={{
            marginBottom: "2rem",
            borderRadius: "16px",
            marginLeft: "1rem",
            marginRight: "1rem",
          }}
        />
      ) : (
        <>
          <ListItem
            dense
            disablePadding
            sx={{
              padding: "2rem 0",
              borderTop:
                index !== 0 ? "1px solid " + theme.palette.primary.light : null,
              color: "#000",
              fontSize: size || "1rem",
            }}
            key={property.propertyId}
            component={Link}
            to={`/${NAV.PROPERTY_DETAILS}/${property?.urlSlug}`}
          >
            <Grid
              container
              spacing={2}
              style={{
                opacity: property?.isRented ? 0.6 : 1,
              }}
            >
              <Grid item xs={isSmallScreen ? 12 : 0}>
                <Box position={"relative"}>
                  <Box
                    sx={{
                      position: "absolute",
                      opacity: 1,
                      zIndex: 10,
                      bottom: "0",
                      borderRadius: ".2rem .7rem .7rem .7rem",
                      right: "-.5rem",
                      border: "1px solid #aaf",
                      padding: ".5rem 1rem",
                      background: "#eef",
                      color: "#000",
                      textAlign: "center",
                      boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 8px 0px",
                    }}
                  >
                    <Typography variant="body2">Complex</Typography>
                  </Box>

                  <PropertyImage
                    width={isSmallScreen ? "100%" : "15rem"}
                    src={
                      property.imageUrl !== ""
                        ? property.imageUrl
                        : require("../../assets/images/no-image.jpg")
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm container>
                <Grid item xs container direction="column" spacing={2}>
                  <Grid item xs>
                    <Typography
                      sx={{
                        textDecoration: "none",
                        color: "#333",
                        fontWeight: "600",
                      }}
                    >
                      {property?.title}
                    </Typography>
                    <Box
                      display={"flex"}
                      justifyContent={"flex-start"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <Typography>{formattedDate}</Typography>
                    </Box>
                    <Box
                      marginTop=".5rem"
                      display={"flex"}
                      justifyContent={"flex-start"}
                      alignItems={"center"}
                      gap={0.1}
                    >
                      <PlaceIcon
                        sx={{
                          color: "#808080",
                        }}
                      />
                      <Typography variant="body2">
                        {property?.location ? (
                          property?.location
                        ) : (
                          <>
                            {property?.propertyDetails?.streetAddress}
                            {(property?.propertyDetails?.zipcode && property?.propertyDetails?.city) && ", "}
                            {property?.propertyDetails?.zipcode && (" ")}{property?.propertyDetails?.zipcode}
                            {property?.propertyDetails?.city && (" ")}{property?.propertyDetails?.city}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    display={"flex"}
                    gap={1}
                    sx={{
                      flexWrap: "wrap",
                    }}
                  >
                    {property?.propertyDetails?.energyLabel && (
                      <Chip
                        size={isSmallScreen ? "small" : "medium"}
                        icon={
                          <EnergySavingsLeafOutlinedIcon
                            style={{ color: theme.palette.primary.main }}
                          />
                        }
                        sx={{
                          fontSize: ".8em",
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light,
                        }}
                        label={
                          !property?.propertyDetails?.energyLabel
                            ? "Geen energielabel"
                            : t(property?.propertyDetails?.energyLabel)
                        }
                      />
                    )}
                    {property?.propertyCollectionDetails?.maxLivingArea && (
                      <Chip
                        size={isSmallScreen ? "small" : "medium"}
                        icon={
                          <StraightenIcon
                            style={{ color: theme.palette.primary.main }}
                          />
                        }
                        sx={{
                          fontSize: ".8em",
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light,
                        }}
                        label={
                          <Box>
                            {property?.propertyCollectionDetails?.maxLivingArea} -{" "}
                            {property?.propertyCollectionDetails?.minLivingArea}{" "}
                            m²
                          </Box>
                        }
                      />
                    )}
                    {property?.propertyCollectionDetails?.minAmountOfRooms > 0 && (
                      <Chip
                        size={isSmallScreen ? "small" : "medium"}
                        icon={
                          <StraightenIcon
                            style={{ color: theme.palette.primary.main }}
                          />
                        }
                        sx={{
                          fontSize: ".8em",
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light,
                        }}
                        label={
                          <Box>
                            {
                              property?.propertyCollectionDetails
                                ?.minAmountOfRooms
                            }{" "}
                            -{" "}
                            {
                              property?.propertyCollectionDetails
                                ?.maxAmountOfRooms
                            }{" "}
                            {t("overview.rooms")}
                          </Box>
                        }
                      />
                    )}
                  </Grid>
                </Grid>
                {(property?.propertyCollectionDetails?.minPrice > 0 && property?.propertyCollectionDetails?.maxPrice > 0) && (
                  <Grid xs={12} md={3} textAlign={"right"} item>
                    <Typography variant="subtitle1" fontWeight={"bold"}>
                      {formatCurrency(
                        property?.propertyCollectionDetails?.minPrice,
                      )}{" "}
                      -{" "}
                      {formatCurrency(property?.propertyCollectionDetails?.maxPrice)}
                    </Typography>
                  </Grid>
                )}
                {((!property?.propertyCollectionDetails?.minPrice || !property?.propertyCollectionDetails?.maxPrice) && (property?.propertyDetails?.minimumRent || property?.minimumRent)) && (
                  <Grid xs={12} md={3} textAlign={"right"} item>
                    <Typography variant="subtitle1" fontWeight={"bold"}>
                      {formatCurrency(property?.propertyDetails?.minimumRent ?? property?.minimumRent)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </ListItem>
        </>
      )}
    </>
  );
};

export const PropertyName = styled(Box)({
  margin: "10px 0",
  cursor: "pointer",

  ":hover": {
    color: theme.palette.primary.main,
  },
});

export const StyledLink = styled(Link)({
  textDecoration: "none",
  color: "inherit",
});

export default CollectionCard;

export const PropertyImage = styled("img")(({ width }) => ({
  width: width || "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "4px",
  maxHeight: "12rem",
  boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 4px 0px",
}));
